// [phase11-v1] Signal provider grid — trading-ui.
//
// Surfaces the 7 Signal providers' real-time SLA state. Bundle markers
// preserved: "provider-health" referenced via signalClient endpoint string.
import { Activity } from "lucide-react";
import { TermHelp } from "../BeginnerHelp";  // beginner-help Phase 1
import {
  formatAge,
  formatSla,
  providerMeta,
  type SignalProviderHealth,
  type SignalProviderStatus,
} from "../../lib/signalClient";

interface Props {
  data: SignalProviderHealth | null;
}

function badgeClasses(tone: "green" | "blue" | "amber" | "red" | "neutral"): string {
  const base =
    "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border";
  switch (tone) {
    case "green":
      return `${base} bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30`;
    case "blue":
      return `${base} bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30`;
    case "amber":
      return `${base} bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30`;
    case "red":
      return `${base} bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30`;
    default:
      return `${base} bg-[var(--card-bg)] text-[var(--muted-text)] border-[var(--card-border)]`;
  }
}

function statusTone(s: SignalProviderStatus): "green" | "blue" | "amber" | "red" | "neutral" {
  if (s === "LIVE") return "green";
  if (s === "LIVE_PROXY") return "blue";
  if (s === "STALE") return "amber";
  if (s === "MISSING") return "red";
  return "neutral";
}

function statusBorder(s: SignalProviderStatus): string {
  if (s === "LIVE") return "border-l-2 border-l-emerald-500";
  if (s === "LIVE_PROXY") return "border-l-2 border-l-blue-500";
  if (s === "STALE") return "border-l-2 border-l-amber-500";
  return "border-l-2 border-l-red-500";
}

const ORDER = [
  "premarket_bars",
  "quotes_spread",
  "news",
  "sec_filings",
  "float",
  "vix",
  "halt_status",
];

export default function SignalProviderGrid({ data }: Props) {
  if (!data) {
    return (
      <section
        data-testid="signal-provider-grid"
        data-state="empty"
        className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
      >
        <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
          <Activity className="h-4 w-4" /> Engine Provider SLA Feeds
          <span className="ml-1" data-testid="signal-provider-grid-help"><TermHelp term="Provider Health" /></span>
        </div>
        <div className="text-xs text-[var(--muted-text)] mt-3">No provider data yet.</div>
      </section>
    );
  }

  const entries = ORDER.map((k) => [k, data.providers[k]] as const).filter(
    ([, v]) => v !== undefined,
  );
  const liveCount = entries.filter(
    ([, v]) => v.status === "LIVE" || v.status === "LIVE_PROXY",
  ).length;

  return (
    <section
      data-testid="signal-provider-grid"
      data-state="ready"
      data-provider-health="present"
      className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
    >
      <header className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
          <Activity className="h-4 w-4 text-emerald-500" /> Engine Provider SLA Feeds
        </div>
        <span className="text-[10px] text-[var(--muted-text)] font-bold tracking-wider">
          GATEKEEPERS: {liveCount}/{entries.length} LIVE · feeds /api/v1/signal/provider-health
        </span>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {entries.map(([key, probe]) => {
          const meta = providerMeta(key);
          const tone = statusTone(probe.status);
          const overSla =
            probe.age_seconds !== null &&
            probe.age_seconds > probe.max_staleness_seconds;
          return (
            <div
              key={key}
              data-testid={`matrix-provider-${key}`}
              data-provider-status={probe.status}
              className={`rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3 flex flex-col gap-2 ${statusBorder(probe.status)}`}
            >
              <div>
                <div className="flex items-start justify-between gap-1.5">
                  <div className="text-sm font-bold text-[var(--app-text)] leading-tight">
                    {meta.label}
                  </div>
                  {probe.critical && (
                    <span
                      className="text-[8px] bg-red-500/15 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded border border-red-500/30 font-bold tracking-widest"
                      title="SLA failure blocks realtime_data_verified"
                    >
                      CRIT
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[var(--muted-text)] uppercase tracking-wider font-semibold mt-1">
                  {meta.description}
                </div>
              </div>
              <div className="border-t border-[var(--card-border)] pt-2 space-y-1.5 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-[var(--muted-text)] text-[10px] font-bold tracking-wide">FEED</span>
                  <span className={badgeClasses(tone)}>{probe.status}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[var(--muted-text)] text-[10px] font-bold tracking-wide">AGE</span>
                  <span
                    className={`font-mono text-[11px] ${
                      overSla ? "text-amber-600 dark:text-amber-400" : "text-[var(--app-text)]"
                    }`}
                  >
                    {formatAge(probe.age_seconds)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-[var(--card-border)] pt-1.5">
                  <span className="text-[var(--muted-text)] text-[10px] font-bold tracking-wide">SLA</span>
                  <span className="font-mono text-[11px] text-[var(--muted-text)]">
                    ≤ {formatSla(probe.max_staleness_seconds)}
                  </span>
                </div>
                {probe.reason && (
                  <div
                    className="text-[9px] font-mono text-amber-700 dark:text-amber-300 bg-amber-500/5 p-1.5 rounded border border-amber-500/20 leading-snug break-words"
                    title={probe.reason}
                  >
                    {probe.reason}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
